import React, { useEffect, useState } from 'react';

import { DatadogSessionReplayMask } from 'features/observability/rum/datadog-session-replay-mask';
import { FieldProps } from 'formik';
import { scrollIntoView } from 'seamless-scroll-polyfill';

import { StyledInput } from 'components/otp-input/styled';
import { t } from 'state/intl/translations';
import { isIOS, isWeb } from 'utils/environment';

const NUMBER_REGEXP = new RegExp(/^[0-9]{0,}$/);

// Additional Y amount for show email input and submit button
const ADDITIONAL_SCROLL_Y = 50;

interface IFormikOTPInput {
  field: FieldProps['field'];
  form: FieldProps['form'];
  otpCodeLength: number;
  setFieldValue(name: string, value: string): void;
}

export const FormikOTPInput = ({
  field,
  form,
  setFieldValue,
  otpCodeLength,
  ...props
}: IFormikOTPInput) => {
  const [didChange, setDidChange] = useState(false); // component remounts not changing if OTP is wrong, this handles that situation
  const handleChange = (value: string) => {
    if (value.length > otpCodeLength) {
      return;
    }

    if (!NUMBER_REGEXP.test(value)) {
      return;
    }

    setDidChange(true);
    setFieldValue(field.name, value);
  };

  // read values with useEffect to ensure latest state is used (doing in handle change is a race condition that can fail)
  useEffect(() => {
    const v = form?.values?.[field.name] as string | undefined;
    if (!form.isSubmitting && v?.length === otpCodeLength && didChange) {
      form.submitForm();
    }
  }, [form, field.name, form.isSubmitting, form?.values?.[field.name], otpCodeLength, didChange]);

  // Do not include input field focus as it's clashing with page transition
  return (
    <DatadogSessionReplayMask>
      <StyledInput
        {...field}
        {...props}
        keyboardType="numeric"
        returnKeyType="done"
        onChangeText={handleChange}
        label={t('validationCode')}
        accessibilityLabel={`${t('validationCode')}. ${t('authEmailSentEmail')}`}
        required
        testID="OTP-code-input"
        onLayout={ev => {
          if (isWeb) {
            // This is a hack to avoid overlapping on mobile web
            const element = document.querySelector('[data-testid="OTP-code-input"]');
            const otpInput = document.querySelector('[data-testid="OTP-code-input"]');
            if (element && !isIOS()) {
              element.scrollTop = element.scrollHeight + ADDITIONAL_SCROLL_Y;
            } else if (otpInput) {
              scrollIntoView(otpInput);
            }
          }
        }}
      />
    </DatadogSessionReplayMask>
  );
};
